import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'motion/react';
import CTAButton from '../../../shared/components/buttons/CTAButton';
import SecondaryButton from '../../../shared/components/buttons/SecondaryButton';
import SectionHeading from '../../../ui/SectionHeading';
import FeatureCard from '../../../ui/FeatureCard';
import StoryCard from '../../../ui/StoryCard';
import PricingSection from '../components/PricingSection';
import Hero from '../components/Hero/Hero';

const buyerSteps = [
  {
    title: 'Discover Near You',
    copy: 'Search by cuisine, neighbourhood, or story. Meet sellers through Kinn profiles and live menus.',
  },
  {
    title: 'Order with Ease',
    copy: 'Secure checkout powered by Stripe. Sellers coordinate pickup or delivery directly with you.',
  },
  {
    title: 'Share the Love',
    copy: 'Follow favourites, leave kind words, and support local food economies.',
  },
];

const sellerSteps = [
  {
    title: 'Set Up Your Profile',
    copy: 'Create your Kinn Master profile with your story, compliance info, and kitchen essentials.',
    time: '5 minutes',
  },
  {
    title: 'Upload Your Menu',
    copy: 'Add dishes, pricing, and availability with photos and provenance in one streamlined editor.',
    time: '10 minutes',
  },
  {
    title: 'Start Selling',
    copy: (
      <span>
        Go live with your <span className="font-semibold color-primary">kinn.store/yourname</span> link and let our
        automations handle the back-office noise.
      </span>
    ),
    time: 'Instant',
  },
];

const audienceTabs = [
  { key: 'seller', label: 'For Sellers' },
  { key: 'buyer', label: 'For Buyers' },
];

const featureCards = [
  {
    title: 'Built-In Storefront',
    description: 'Your own beautiful website, optimised for mobile and ready to share.',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M5 10v8a2 2 0 002 2h10a2 2 0 002-2v-8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4h6v2M9 14h6" />
      </svg>
    ),
  },
  {
    title: 'Compliance Made Easy',
    description: 'Food safety docs, licensing help, and regulatory updates — all handled.',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5a2 2 0 012-2h2a2 2 0 012 2m-5 8h4" />
      </svg>
    ),
  },
  {
    title: 'AI-Driven Guidance',
    description: 'Smart suggestions to help you price, market, and grow your business.',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 13V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14" />
      </svg>
    ),
  },
  {
    title: 'Allergen Tagging',
    description: 'Automatically track allergens and ingredients to keep customers safe.',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9V4m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 16.5a2 2 0 104 0v-.531c0-.895.356-1.754.988-2.386l.548-.547A5 5 0 109.012 13.583l.548.547c.632.632.988 1.491.988 2.386z" />
      </svg>
    ),
  },
  {
    title: 'In-App Chat Requests',
    description: 'Connect with customers directly to answer questions and build relationships.',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8m-8 4h5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.97-4.477 9-10 9-1.154 0-2.258-.184-3.279-.523L3 21l.887-4.022A8.715 8.715 0 013 12c0-4.97 4.477-9 10-9s10 4.03 10 9z" />
      </svg>
    ),
  },
  {
    title: 'Health & Marketing Kits',
    description: 'Get compliance documentation and marketing materials generated for you.',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4 2 2 0 000-4m-6 8a2 2 0 100-4 2 2 0 000 4m0 0v6m6-6v6m6-2a2 2 0 100-4 2 2 0 000 4m0 0v2" />
      </svg>
    ),
  },
];

const testimonials = [
  {
    quote: '“Kinn turned my supper club into a system. Orders, compliance, and messaging now live in one calm HQ.”',
    author: 'Ama • Kinn Master',
    business: 'Accra Kitchen',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=600&q=80',
  },
  {
    quote: '“I upload my spice menus once and Kinn keeps everything synced for buyers with live provenance updates.”',
    author: 'Khadija • Kinn Master',
    business: 'Spice Trail',
    image: 'https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?w=600&q=80',
  },
  {
    quote: '“My kinn.store/mezze link feels premium. Buyers see my story, certifications, and menu before we even chat.”',
    author: 'Lina • Kinn Master',
    business: 'Mezze by Lina',
    image: 'https://images.unsplash.com/photo-1623428187969-5da2dcea5ebf?w=600&q=80',
  },
];

const pricingFeatures = [
  { title: 'Menu & Stock Management', desc: 'Update offerings and track inventory in real-time' },
  { title: 'Integrated Payments', desc: 'Secure, straightforward payments processed directly to you' },
  { title: 'Unified Order Dashboard', desc: 'See and manage all orders from one screen' },
  { title: 'AI Allergen Detective', desc: 'Upload recipe, AI flags UK allergens instantly' },
  { title: 'AI Ingredient Generator', desc: 'Create professional ingredient lists in seconds' },
  { title: 'Exclusive Marketplace Access', desc: 'Get discovered by food lovers', badge: 'Coming Soon' },
];

const Homepage = () => {
  const [activeAudience, setActiveAudience] = useState('seller');
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const steps = useMemo(() => (activeAudience === 'seller' ? sellerSteps : buyerSteps), [activeAudience]);

  return (
    <div className="relative min-h-screen bg-[var(--canvas)]">
      <Hero />

      <section className="py-24" style={{ background: 'var(--surface)' }}>
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            className="mb-14"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading
              align="center"
              eyebrow="Why Kinn"
              title="Everything You Need to Succeed"
              description="A complete platform designed for food sellers and buyers. Every tool you need to launch and grow your food business."
              tone="terracotta"
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featureCards.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <FeatureCard {...feature} index={index} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ background: 'var(--canvas)' }}>
        <div className="mx-auto w-full max-w-6xl space-y-12 px-6">
          <motion.div
            className="flex flex-wrap items-center justify-between gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading
              align="left"
              tone="terracotta"
              className="max-w-2xl"
              eyebrow="How it works"
              title="A simple path for sellers and buyers."
              description="Whether you are launching your first menu or ordering from your favourite cook, every step feels thoughtful, guided, and safe."
            />
            <div className="inline-flex rounded-full border border-[rgba(200,106,74,0.3)] bg-white/80 p-1 shadow-[0_18px_55px_-35px_rgba(200,106,74,0.45)]">
              {audienceTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveAudience(tab.key)}
                  className={[
                    'rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] transition-all duration-300',
                    activeAudience === tab.key
                      ? 'bg-[var(--terracotta)] text-white shadow-[0_12px_30px_-18px_rgba(200,106,74,0.75)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <motion.article
                key={step.title}
                className="relative overflow-hidden rounded-[32px] border bg-[var(--canvas)] p-8 shadow-[0_35px_75px_-45px_rgba(200,106,74,0.55)]"
                style={{ borderColor: 'rgba(200, 106, 74, 0.25)' }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-xs uppercase tracking-[0.3em] font-decorative" style={{ color: 'var(--terracotta)' }}>
                  {activeAudience === 'seller' ? step.time || 'Step' : 'Experience'}
                </div>
                <h3
                  className="mt-4 font-display text-2xl"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {step.copy}
                </p>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-brand-slate/10 via-transparent to-transparent" />
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ background: 'var(--surface)' }}>
        <div className="mx-auto max-w-7xl space-y-12 px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-120px' }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading
              align="center"
              tone="terracotta"
              eyebrow="Success stories"
              title="Stories from our Kinn Masters"
              description="Meet the makers redefining local food economies—each story highlights heritage, resourcefulness, and community care."
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <StoryCard quote={testimonial.quote} author={testimonial.author} business={testimonial.business} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection
        billingPeriod={billingPeriod}
        onToggleBillingPeriod={() => setBillingPeriod((period) => (period === 'monthly' ? 'yearly' : 'monthly'))}
        features={pricingFeatures}
      />

      <section className="mx-auto mt-24 w-full max-w-6xl px-6">
        <div className="relative overflow-hidden rounded-[40px] border border-[color:var(--shell-border)] bg-brand-cream/70 px-8 py-16 text-center shadow-[var(--shadow-soft)] backdrop-blur-xl sm:px-16">
          <h2 className="font-display text-3xl sm:text-4xl" style={{ color: 'var(--color-text-primary)' }}>
            Your All-in-One Kitchen HQ
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base" style={{ color: 'var(--color-text-secondary)' }}>
            We replace the clutter of notes, spreadsheets, and multiple apps with a single, powerful system built just for food.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CTAButton as={NavLink} to="/seller/get-started" size="lg" className="px-10">
              Start Selling Today
            </CTAButton>
            <SecondaryButton as="a" href="mailto:hello@joinkinn.com" size="lg" className="px-10">
              Talk to our team
            </SecondaryButton>
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/45 via-transparent to-white/10" />
        </div>
      </section>
    </div>
  );
};

export default Homepage;
